import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef
} from 'react';

import { StateEventConfig, StateNodeType } from '@people-science/state-machine';
import { FormElementMeta } from '@people-science/forms-core';
import { FetchError } from '../../api/api';
import {
  useDynamicFormContext,
  DynamicFormContextState
} from '../../dynamic-forms/DynamicFormContext';
import { useUpsertFormEntityData } from '../../dynamic-forms/hooks/useUpsertFormEntityData';
import { FormTemplate } from '../../models/FormTemplate';
import { useUserContext } from '../../context/UserContext';
import { FormData } from '../../dynamic-forms/models/FormData';
import { reservedDataSourceKeys } from '../../common/hasCompletedFormTemplateFields';

const QualificationFormDataSource: React.FC<
  PropsWithChildren & {
    studyParticipantId: number;
    formTemplate: FormTemplate;
    disabled?: boolean;
    formData?: FormData<any>;
    onCreated?: (formData: FormData<any>) => void;
    /** Fires after each successful upsert; isFinal marks the completing one. */
    onSubmitted?: (isFinal: boolean) => void;
    onError?: (error: FetchError) => void;
  }
> = ({
  studyParticipantId,
  formTemplate,
  disabled,
  formData,
  children,
  onCreated,
  onSubmitted,
  onError
}) => {
  const { user, updateUserSession } = useUserContext();
  const {
    stateMachine,
    entitySchema,
    elementPlugins,
    activeStateNodes,
    formValues,
    setFormValues,
    changedFormValue,
    subscribe,
    unsubscribe,
    setIsSubmitting,
    setError,
    setTransitioningStateEvent
  } = useDynamicFormContext<any, any>();
  const onCreatedRef = useRef(onCreated);
  const onSubmittedRef = useRef(onSubmitted);
  const onErrorRef = useRef(onError);
  const shouldResubmitRef = useRef(false);
  const lastIsFinalRef = useRef(false);
  onCreatedRef.current = onCreated;
  onSubmittedRef.current = onSubmitted;
  onErrorRef.current = onError;
  const previousFormValueRef = useRef(changedFormValue);
  const entityIdRef = useRef(formData?.id);
  const stateMachineRef = useRef(stateMachine);
  const startDateRef = useRef(new Date());
  stateMachineRef.current = stateMachine;
  // const [entityId, setEntityId] = useState<number | undefined>(formData?.id);

  // get all form elements currently displayed in the DynamicForm component
  const formElementMetasRef = useRef<FormElementMeta<any, any>[]>([]);
  formElementMetasRef.current = activeStateNodes?.reduce(
    (elements, stateNode) => {
      elements.push(...(stateNode.meta?.elements || []));
      return elements;
    },
    [] as FormElementMeta<any, any>[]
  );

  const {
    upsertedEntityData,
    isUpsertingData,
    upsertingDataError,
    upsertEntityData
  } = useUpsertFormEntityData(entitySchema?.tableName || '');

  // handle state machine transitioning
  const finishTransition = useCallback(() => {
    setTransitioningStateEvent(event => {
      stateMachine?.transition(event?.name);
      return undefined;
    });
    setIsSubmitting(false);
  }, [setTransitioningStateEvent, setIsSubmitting, stateMachine]);
  // update entityId state after creating entity data
  useEffect(() => {
    if (upsertedEntityData?.id || formData?.id) {
      entityIdRef.current = upsertedEntityData?.id || formData?.id;
      // console.log('createdEntityData', createdEntityData);
      // setEntityId(createdEntityData?.id || formData?.id);
    }
  }, [upsertedEntityData, formData]);
  useEffect(() => {
    if (formValues && upsertedEntityData?.studyParticipantId) {
      formValues.studyParticipantId = upsertedEntityData.studyParticipantId;
    }
  }, [upsertedEntityData, formValues]);
  useEffect(() => {
    if (upsertedEntityData) {
      shouldResubmitRef.current = false;
      finishTransition();
      onSubmittedRef.current?.(lastIsFinalRef.current);
    }
    if (!formData?.id && upsertedEntityData?.id) {
      onCreatedRef.current?.(upsertedEntityData);
    }
  }, [formData?.id, upsertedEntityData, finishTransition, upsertEntityData]);
  // Error handling
  useEffect(() => {
    if (upsertingDataError) {
      shouldResubmitRef.current = true;
      setError(upsertingDataError);
      onErrorRef.current?.(upsertingDataError);
    }
  }, [setError, upsertingDataError]);
  const subscriptionDependencyRef = useRef({
    changedFormValue,
    formValues,
    entitySchema
  });
  subscriptionDependencyRef.current = {
    changedFormValue,
    formValues,
    entitySchema
  };
  useEffect(() => {
    if (disabled) {
      return;
    }
    const submitEventData = async (
      context: DynamicFormContextState<any, any>,
      userId: number,
      statePath: string,
      isFinal: boolean
    ) => {
      const { entitySchema, formValues } = subscriptionDependencyRef.current;
      lastIsFinalRef.current = isFinal;
      const data = {} as any;
      for (const column of entitySchema?.columns || []) {
        // make sure to only upsert form data of actual entity schema fields
        let value = formValues[column.name];
        if (typeof value !== 'undefined') {
          // run any data transformations or file uploads from plugins before
          // upserting
          const formElementMeta = formElementMetasRef.current.find(
            e => e.dataSource === column.name
          );
          const plugin = elementPlugins.find(
            p => p.type === formElementMeta?.type
          );
          if (plugin?.onBeforeSave) {
            value = await plugin?.onBeforeSave(value, context);
          }
          // assign value for sending for upsert
          data[column.name] = value;
        }
      }
      // check if the column is one of the reserved data source keys
      for (const column of reservedDataSourceKeys) {
        if (formValues[column]) {
          data[column] = formValues[column];
        }
      }
      data.userId = userId;
      data.studyParticipantId = studyParticipantId;
      if (entityIdRef.current) {
        data.id = entityIdRef.current;
      }
      const duration = Math.round(
        (new Date().getTime() - startDateRef.current.getTime()) / 1000
      );
      setIsSubmitting(true);
      await upsertEntityData({
        formTemplateId: formTemplate.id,
        version: formTemplate.version,
        statePath,
        isFinal,
        data,
        duration
      });
      await updateUserSession();
    };
    const handleTransitionEvent = async (
      context: DynamicFormContextState<any, any>,
      event: StateEventConfig
    ) => {
      const { changedFormValue } = subscriptionDependencyRef.current;
      const stateMachine = stateMachineRef.current;
      const stateNode = stateMachine?.currentState?.path
        ? stateMachine?.getStateNode(stateMachine?.currentState?.path)
        : undefined;
      const isFinal = stateNode?.type === StateNodeType.Final;
      const statePath = stateMachine?.currentState?.path || '';
      if (
        user?.id &&
        ((changedFormValue &&
          previousFormValueRef.current !== changedFormValue) ||
          isFinal ||
          shouldResubmitRef.current === true)
      ) {
        previousFormValueRef.current = changedFormValue;
        await submitEventData(context, user.id, statePath, isFinal);
      } else {
        finishTransition();
      }
    };
    const handleFinalEvent = async (
      context: DynamicFormContextState<any, any>,
      event: StateEventConfig
    ) => {
      const stateMachine = stateMachineRef.current;
      const stateNode = stateMachine?.currentState?.path
        ? stateMachine?.getStateNode(stateMachine?.currentState?.path)
        : undefined;
      const isFinal = stateNode?.type === StateNodeType.Final;
      const statePath = stateMachine?.currentState?.path || '';
      // for backwards compatibility do not submit event data / set final state
      // if section (stateNode) has form elements that are required to complete.
      // Existing forms have "Final" state node type configured on sections
      // that contain form elements. The form will be marked completed upon
      // transitioning away from the section (see handleTransitionEvent)
      const hasRequiredFields = stateNode?.meta?.elemenets?.some(
        (e: any) => e.required
      );
      if (user?.id && isFinal && !hasRequiredFields) {
        await submitEventData(context, user.id, statePath, isFinal);
      }
    };
    subscribe('transition', handleTransitionEvent);
    subscribe('final', handleFinalEvent);
    return () => {
      unsubscribe('transition', handleTransitionEvent);
      unsubscribe('final', handleFinalEvent);
    };
  }, [
    user?.id,
    elementPlugins,
    disabled,
    finishTransition,
    subscribe,
    unsubscribe,
    upsertEntityData,
    formTemplate.id,
    formTemplate.version,
    setIsSubmitting
  ]);
  return <>{children}</>;
};

export default QualificationFormDataSource;
